/*
 * Copyright (C) Contributors to the Suwayomi project
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */

import type {
    Work,
    WorkType,
    WorkMetadata,
    SourceEdition,
    Progress,
    CollectionEntry,
    CollectionStatus,
    ExternalLinks,
    DownloadJob,
    DownloadDestination,
    DownloadJobStatus,
    DownloadRealProgress,
    PlaybackCandidate,
    PlaybackFormat,
    PlaybackControlCapabilities,
    SourceCapabilities,
} from '../types/work.ts';

/**
 * Robust UUID/Unique ID generator for canonical Works and entities.
 */
export function generateWorkId(prefix: string = 'work'): string {
    const timePart = Date.now().toString(36);
    let randomPart = '';
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        const buf = new Uint8Array(8);
        crypto.getRandomValues(buf);
        randomPart = Array.from(buf, b => b.toString(16).padStart(2, '0')).join('').slice(0, 8);
    } else {
        randomPart = Math.random().toString(36).slice(2, 10);
    }
    return `${prefix}_${timePart}_${randomPart}`;
}

/**
 * Normalizes title string for robust heuristic matching.
 * Removes diacritics, punctuation, season/part suffixes, and excess whitespace.
 */
export function normalizeTitle(title: string): string {
    if (!title) return '';
    return title
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // remove accents
        .toLowerCase()
        // Strip common season/cour/part suffixes for root match
        .replace(/\b(season|temporada|part|parte|cour)\s*\d+\b/gi, '')
        .replace(/\b(\d+)(st|nd|rd|th)?\s*(season|temporada)\b/gi, '')
        .replace(/[^a-z0-9\s]/g, ' ') // replace punctuation with space
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Checks matching quality between two titles.
 * Returns score from 0.0 to 1.0. Title heuristics are strictly suggestions (score < 1.0).
 */
export function compareTitles(titleA: string, titleB: string): { score: number; isExact: boolean; isSuggested: boolean } {
    if (!titleA || !titleB) return { score: 0, isExact: false, isSuggested: false };
    
    const rawA = titleA.trim().toLowerCase();
    const rawB = titleB.trim().toLowerCase();
    if (rawA === rawB) {
        return { score: 0.98, isExact: true, isSuggested: true };
    }

    const normA = normalizeTitle(titleA);
    const normB = normalizeTitle(titleB);
    if (!normA || !normB) return { score: 0, isExact: false, isSuggested: false };

    if (normA === normB) {
        return { score: 0.95, isExact: true, isSuggested: true };
    }

    // Substring containment
    if (normA.includes(normB) || normB.includes(normA)) {
        const ratio = Math.min(normA.length, normB.length) / Math.max(normA.length, normB.length);
        const score = Math.max(0.7, Number((0.7 + ratio * 0.2).toFixed(2)));
        return { score, isExact: false, isSuggested: true };
    }

    // Token set overlap (Jaccard similarity)
    const tokensA = new Set(normA.split(' ').filter(t => t.length > 1));
    const tokensB = new Set(normB.split(' ').filter(t => t.length > 1));
    if (tokensA.size === 0 || tokensB.size === 0) return { score: 0, isExact: false, isSuggested: false };

    let intersection = 0;
    for (const token of tokensA) {
        if (tokensB.has(token)) intersection++;
    }
    const union = new Set([...tokensA, ...tokensB]).size;
    const jaccard = union > 0 ? intersection / union : 0;

    if (jaccard >= 0.6) {
        return { score: Number((jaccard * 0.85).toFixed(2)), isExact: false, isSuggested: true };
    }

    return { score: 0, isExact: false, isSuggested: false };
}

/**
 * Factory for creating a canonical Work entity.
 * Guarantees AniList is an optional external link, never the canonical ID or a fake hash.
 */
export function createWork(params: {
    id?: string;
    type: WorkType;
    title: string;
    originalTitle?: string;
    romajiTitle?: string;
    englishTitle?: string;
    nativeTitle?: string;
    altTitles?: string[];
    metadata?: WorkMetadata;
    externalLinks?: ExternalLinks;
    confirmedEditionIds?: string[];
    suggestedEditionIds?: string[];
}): Work {
    const now = new Date().toISOString();
    return {
        id: params.id || generateWorkId('work'),
        type: params.type,
        title: params.title.trim(),
        originalTitle: params.originalTitle?.trim(),
        romajiTitle: params.romajiTitle?.trim(),
        englishTitle: params.englishTitle?.trim(),
        nativeTitle: params.nativeTitle?.trim(),
        altTitles: params.altTitles?.map(t => t.trim()).filter(Boolean) || [],
        metadata: params.metadata || {},
        externalLinks: params.externalLinks || {},
        confirmedEditionIds: params.confirmedEditionIds || [],
        suggestedEditionIds: params.suggestedEditionIds || [],
        createdAt: now,
        updatedAt: now,
    };
}

/**
 * Factory for creating a SourceEdition.
 */
export function createSourceEdition(params: {
    sourceId: string;
    originalId: string;
    workId: string;
    format: 'manga' | 'anime' | 'live' | string;
    language?: string;
    title?: string;
    url?: string;
    coverUrl?: string;
    isExplicitlyLinked?: boolean;
    confidenceScore?: number;
    metadata?: Record<string, any>;
}): SourceEdition {
    const now = new Date().toISOString();
    const id = `${params.sourceId}::${params.originalId}`;
    return {
        id,
        sourceId: params.sourceId,
        originalId: params.originalId,
        workId: params.workId,
        format: params.format,
        language: params.language,
        title: params.title?.trim(),
        url: params.url,
        coverUrl: params.coverUrl,
        isExplicitlyLinked: !!params.isExplicitlyLinked,
        confidenceScore: params.isExplicitlyLinked ? 1.0 : (params.confidenceScore ?? 0.8),
        confirmedAt: params.isExplicitlyLinked ? now : undefined,
        metadata: params.metadata || {},
        createdAt: now,
        updatedAt: now,
    };
}

/**
 * Factory for creating edition-level Progress.
 */
export function createProgress(params: {
    workId: string;
    editionId: string;
    sourceId: string;
    originalId: string;
    itemId: string;
    itemNumber: number;
    itemName?: string;
    position: number;
    total?: number;
    isCompleted?: boolean;
    updatedAt?: string;
    readAt?: string;
    date?: string;
}): Progress {
    const now = params.updatedAt || params.readAt || params.date || new Date().toISOString();
    return {
        id: `${params.workId}::${params.editionId}::${params.itemId}`,
        workId: params.workId,
        editionId: params.editionId,
        sourceId: params.sourceId,
        originalId: params.originalId,
        itemId: params.itemId,
        itemNumber: params.itemNumber,
        itemName: params.itemName,
        position: Math.max(0, params.position),
        total: params.total,
        isCompleted: !!params.isCompleted || (params.total !== undefined && params.total > 0 && params.position >= params.total),
        updatedAt: now,
        readAt: params.readAt || now,
        date: params.date || now,
    };
}

/**
 * Factory for creating a CollectionEntry.
 */
export function createCollectionEntry(params: {
    workId: string;
    type: 'manga' | 'anime' | string;
    status: CollectionStatus;
    customLists?: string[];
    isFavorite?: boolean;
    rating?: number;
    preferredEditionId?: string;
}): CollectionEntry {
    const now = new Date().toISOString();
    return {
        workId: params.workId,
        type: params.type,
        status: params.status,
        customLists: params.customLists || [],
        isFavorite: !!params.isFavorite,
        rating: params.rating,
        preferredEditionId: params.preferredEditionId,
        addedAt: now,
        updatedAt: now,
    };
}

/**
 * Status mapping helpers between legacy states and unified CollectionStatus.
 */
export function normalizeCollectionStatus(rawStatus?: string | null): CollectionStatus {
    if (!rawStatus) return 'PLANNING';
    const s = String(rawStatus).toUpperCase().trim();
    if (s === 'CURRENT' || s === 'READING' || s === 'WATCHING' || s === 'IN_PROGRESS') return 'CURRENT';
    if (s === 'COMPLETED' || s === 'FINISHED') return 'COMPLETED';
    if (s === 'PAUSED' || s === 'ON_HOLD') return 'PAUSED';
    if (s === 'DROPPED' || s === 'ABANDONED') return 'DROPPED';
    if (s === 'PLANNING' || s === 'PLAN_TO_READ' || s === 'PLAN_TO_WATCH') return 'PLANNING';
    return 'PLANNING';
}

export function getCollectionStatusLabel(status: CollectionStatus, type: 'manga' | 'anime' = 'manga'): string {
    switch (status) {
        case 'CURRENT':
            return type === 'anime' ? 'Viendo' : 'Leyendo';
        case 'PLANNING':
            return 'Pendiente';
        case 'COMPLETED':
            return 'Completado';
        case 'PAUSED':
            return 'En pausa';
        case 'DROPPED':
            return 'Abandonado';
        default:
            return 'Guardado';
    }
}

/**
 * Factory for creating a DownloadJob with real-time progress metrics.
 */
export function createDownloadJob(params: {
    id?: string;
    workId: string;
    editionId?: string;
    itemId?: string;
    itemTitle?: string;
    destination?: DownloadDestination;
    targetPath?: string;
    status?: DownloadJobStatus;
    realProgress?: Partial<DownloadRealProgress>;
}): DownloadJob {
    const now = new Date().toISOString();
    return {
        id: params.id || `dl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        workId: params.workId,
        editionId: params.editionId,
        itemId: params.itemId,
        itemTitle: params.itemTitle,
        destination: params.destination || 'indexeddb',
        targetPath: params.targetPath,
        status: params.status || 'pending',
        realProgress: {
            totalBytes: params.realProgress?.totalBytes || 0,
            downloadedBytes: params.realProgress?.downloadedBytes || 0,
            percent: params.realProgress?.percent || 0,
            speedBps: params.realProgress?.speedBps,
            etaSeconds: params.realProgress?.etaSeconds,
            currentItem: params.realProgress?.currentItem,
            totalItems: params.realProgress?.totalItems,
        },
        createdAt: now,
        updatedAt: now,
    };
}

/**
 * Factory for creating a PlaybackCandidate with explicit control capabilities.
 */
export function createPlaybackCandidate(params: {
    server: string;
    format: PlaybackFormat;
    url: string;
    controls?: Partial<PlaybackControlCapabilities>;
    headers?: Record<string, string>;
    quality?: string;
    language?: string;
    isNative?: boolean;
    expiresAt?: number;
    rawUrl?: string;
    downloadUrl?: string;
}): PlaybackCandidate {
    return {
        server: params.server,
        format: params.format,
        url: params.url,
        controls: {
            seek: params.controls?.seek ?? true,
            volume: params.controls?.volume ?? true,
            speed: params.controls?.speed ?? true,
            quality: params.controls?.quality ?? false,
            fullscreen: params.controls?.fullscreen ?? true,
            nativeControls: params.controls?.nativeControls ?? true,
            subtitles: params.controls?.subtitles ?? false,
            audioTracks: params.controls?.audioTracks ?? false,
        },
        headers: params.headers,
        quality: params.quality,
        language: params.language,
        isNative: params.isNative ?? (params.format === 'mp4' || params.format === 'hls' || params.format === 'webm'),
        expiresAt: params.expiresAt,
        rawUrl: params.rawUrl,
        downloadUrl: params.downloadUrl,
    };
}

/**
 * Factory for creating provider SourceCapabilities.
 */
export function createSourceCapabilities(params: {
    search?: boolean;
    read?: boolean;
    playback?: boolean;
    download?: boolean;
    languages?: string[];
    latest?: boolean;
    supportsNewlyAdded?: boolean;
    supportsFilters?: boolean;
    requiresBridge?: boolean;
}): SourceCapabilities {
    return {
        search: !!params.search,
        read: !!params.read,
        playback: !!params.playback,
        download: !!params.download,
        languages: params.languages || ['es'],
        latest: params.latest ?? true,
        supportsNewlyAdded: params.supportsNewlyAdded ?? false,
        supportsFilters: params.supportsFilters ?? false,
        requiresBridge: params.requiresBridge ?? false,
    };
}
